import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { isEmpty, omit } from 'lodash';
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';

import { sanitize } from '../../utils/dompurify';

import './styles/RichText.scss';

/**
 * Make HTML content accesible
 * @param {React.RefObject} ref - Html container ref
 */
const makeHTMLAccessible = ref => {
  if (!ref || !ref.current) return;

  const temp = ref.current;

  // Add alt to images if not present
  const images = temp.getElementsByTagName('img') || [];
  for (let i = 0; i < images.length; i += 1) {
    const alt = images[i].getAttribute('alt');
    if (!alt || alt === '') {
      images[i].setAttribute('alt', `Asset ${i}`);
    }
  }

  // Add title to iframe if not present
  const iFrames = temp.getElementsByTagName('iframe') || [];
  for (let i = 0; i < iFrames.length; i += 1) {
    const title = iFrames[i].getAttribute('title');
    if (!title || title === '') {
      iFrames[i].setAttribute('title', `Video ${i}`);
    }
  }
};

class RichText extends PureComponent {
  constructor(props) {
    super(props);

    this.htmlRef = React.createRef();
  }

  componentDidMount() {
    const { text, skipAccessibleCheck = false } = this.props;

    if (text && !skipAccessibleCheck) makeHTMLAccessible(this.htmlRef);
  }

  componentDidUpdate(prevProps) {
    const { text, skipAccessibleCheck = false } = this.props;

    if (text && text !== prevProps.text && !skipAccessibleCheck)
      makeHTMLAccessible(this.htmlRef);
  }

  render() {
    const {
      text,
      children,
      className,
      skipAccessibleCheck,
      removeQuillClass = false,
      extraTags = [],
      attrsForExtraTags = [],
      ...rest
    } = this.props;
    let sanitizeConfig = {};

    if (!text && !children) return null;

    const richTextProps = {
      className: clsx('rich-text', !removeQuillClass && 'ql-editor', className),
      ref: this.htmlRef,
      // non-interactive element shouldn't contain these aria attributes
      ...omit(rest, ['aria-label', 'aria-labelledby', 'aria-describedby']),
    };

    if (children) return <div {...richTextProps}>{children}</div>;

    if (!isEmpty(extraTags)) {
      sanitizeConfig = { ADD_TAGS: extraTags };
    }
    if (!isEmpty(attrsForExtraTags)) {
      sanitizeConfig = { ...sanitizeConfig, ADD_ATTR: attrsForExtraTags };
    }

    return (
      <div {...richTextProps}>
        <FroalaEditorView
          model={sanitize(text.replace?.(/\n/g, '<br />'), sanitizeConfig)}
        />
      </div>
    );
  }
}

RichText.propTypes = {
  extraTags: PropTypes.arrayOf(PropTypes.string),
  attrsForExtraTags: PropTypes.arrayOf(PropTypes.string),
};

RichText.defaultProps = {
  extraTags: [],
  attrsForExtraTags: [],
};

export default RichText;
